/**
 * CR-7739: Admin Setup > Purchasing > Open Orders, the per site switches.
 *
 * "Hide Propose Qty Ordered" and "Hide Communication Box" are decided for each
 * customer site: the site's own value, else the group's, else the platform
 * value. A group administrator sets the group value and any site; a site
 * administrator sets the sites it holds. The API decides who may edit what and
 * this component only renders those permissions. Every change is recorded in
 * User Activity.
 */
import { useCallback, useEffect, useState } from 'react';
import { openOrdersSettingsAPI } from '../../../lib/api';
import type {
  OpenOrdersPerSiteSettings, OpenOrdersPerSiteSwitch, OpenOrdersPerSiteUpdate,
} from '../../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const SOURCE_LABEL: Record<string, string> = {
  site: 'Site value',
  group: 'Group value',
  platform: 'Platform value',
};

const onOff = (on: boolean) => (on ? 'Hidden' : 'Shown');

function apiError(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
  const message = data?.['message'] ?? data?.['detail'] ?? (data ? Object.values(data)[0] : undefined);
  if (typeof message === 'string') return message;
  if (Array.isArray(message) && typeof message[0] === 'string') return message[0];
  return fallback;
}

/** Inherit / Hidden / Shown for one stored value (null = inherit). */
function ValueSelect({ id, label, value, inheritLabel, disabled, onChange }: {
  id: string; label: string; value: boolean | null; inheritLabel: string; disabled: boolean;
  onChange: (next: boolean | null) => void;
}) {
  const current = value === null ? 'inherit' : value ? 'on' : 'off';
  return (
    <select
      id={id}
      data-testid={id}
      aria-label={label}
      value={current}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value === 'inherit' ? null : e.target.value === 'on')}
      className="flex h-9 w-[190px] rounded-md border border-input bg-background px-2 py-1 text-sm disabled:opacity-60"
    >
      <option value="inherit">{inheritLabel}</option>
      <option value="on">Hidden</option>
      <option value="off">Shown</option>
    </select>
  );
}

export default function OpenOrdersPerSiteSection() {
  const [organisation, setOrganisation] = useState<string | undefined>(undefined);
  const [data, setData] = useState<OpenOrdersPerSiteSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await openOrdersSettingsAPI.get(organisation);
      setData(resp.data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setError(status === 403
        ? 'Only an administrator can view these settings.'
        : 'Failed to load the per site Open Orders settings.');
    } finally {
      setLoading(false);
    }
  }, [organisation]);

  useEffect(() => { load(); }, [load]);

  const save = async (patch: Omit<OpenOrdersPerSiteUpdate, 'organisation'>, message: string) => {
    try {
      setSaving(true);
      const resp = await openOrdersSettingsAPI.update({ ...patch, organisation });
      setData(resp.data);
      toast.success(message);
    } catch (err: unknown) {
      toast.error(apiError(err, 'Failed to save the Open Orders setting'));
      await load();
    } finally {
      setSaving(false);
    }
  };

  const label = (key: OpenOrdersPerSiteSwitch) =>
    data?.switches.find((s) => s.key === key)?.label ?? key;
  const describe = (value: boolean | null) => (value === null ? 'inherited' : onOff(value).toLowerCase());

  return (
    <Card data-testid="open-orders-per-site-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">Open Orders: per site</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          These two controls are decided for each site, so every PO line follows its own site.
          A site uses its own value, else the group value, else the platform value.
          Hide Propose Qty Ordered removes quantity changes for that site&apos;s lines: suppliers
          cannot propose one on a PO line, neither side can on a schedule line, and a quantity
          already proposed cannot be accepted (it can still be rejected).
        </p>
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {!loading && error && (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" /> {error}
          </div>
        )}
        {!loading && data && (
          <>
            {data.organisations.length > 1 && (
              <div className="space-y-1">
                <Label htmlFor="oov-organisation" className="text-sm text-muted-foreground">Organisation</Label>
                <select
                  id="oov-organisation"
                  value={organisation ?? `${data.organisation.kind}:${data.organisation.id}`}
                  onChange={(e) => setOrganisation(e.target.value)}
                  className="flex h-10 w-[320px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  {data.organisations.map((o) => <option key={o.key} value={o.key}>{o.name}</option>)}
                </select>
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Level</TableHead>
                  {data.switches.map((s) => <TableHead key={s.key}>{s.label}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.group && (
                  <TableRow data-testid="oov-group-row" className="bg-muted/40">
                    <TableCell className="text-sm font-medium">
                      Group: {data.organisation.name}
                    </TableCell>
                    {data.switches.map((s) => (
                      <TableCell key={s.key}>
                        <ValueSelect
                          id={`oov-group-${s.key}`}
                          label={`${s.label}, group ${data.organisation.name}`}
                          value={data.group![s.key]}
                          inheritLabel={`Platform (${onOff(data.platform[s.key])})`}
                          disabled={!data.can_edit_group || saving}
                          onChange={(next) => save({ scope: 'group', switch: s.key, value: next },
                            `${label(s.key)} for the group: ${describe(next)}`)}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                )}
                {data.sites.map((site) => (
                  <TableRow key={site.company_id} data-testid={`oov-site-${site.company_id}`}>
                    <TableCell className="text-sm">{site.company_name}</TableCell>
                    {data.switches.map((s) => {
                      const effective = site.effective[s.key];
                      const inherited = site.values[s.key] === null;
                      // What "Inherit" would give: the group value, else the
                      // platform value (never the site's own value).
                      const groupValue = data.group?.[s.key] ?? null;
                      const inheritOn = groupValue ?? data.platform[s.key];
                      return (
                        <TableCell key={s.key}>
                          <div className="space-y-1">
                            <ValueSelect
                              id={`oov-site-${site.company_id}-${s.key}`}
                              label={`${s.label}, ${site.company_name}`}
                              value={site.values[s.key]}
                              inheritLabel={`Inherit (${onOff(inheritOn)})`}
                              disabled={!site.can_edit || saving}
                              onChange={(next) => save(
                                { scope: 'site', company_id: site.company_id, switch: s.key, value: next },
                                `${label(s.key)} for ${site.company_name}: ${describe(next)}`)}
                            />
                            <p className="text-xs text-muted-foreground">
                              {onOff(effective.on)}, {inherited ? SOURCE_LABEL[effective.source] : 'set for this site'}
                            </p>
                          </div>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
                {data.sites.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={data.switches.length + 1} className="text-sm text-muted-foreground">
                      No sites you administer.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {data.group && !data.can_edit_group && (
              <p className="text-xs text-muted-foreground">
                Only a group administrator can change the group value. Your sites can still set their own.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
